import { useRef, useState } from 'react';
import type { Attachment } from '../api/types';
import { ACCEPT, MAX_FILES, checkFiles, formatSize, isPdf } from '../lib/attachmentRules';
import { Button, Spinner } from './ui';

interface Props {
  attachments: Attachment[];
  pending?: File[];
  onAdd: (files: File[]) => void;
  onRemove: (item: Attachment | File) => void;
  busy?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  onOpen?: (a: Attachment) => void;
}

function Icon({ pdf }: { pdf: boolean }) {
  return (
    <span aria-hidden="true" className="flex-none text-xs font-semibold text-muted">{pdf ? 'PDF' : 'IMG'}</span>
  );
}

export default function AttachmentsField({
  attachments, pending = [], onAdd, onRemove, busy = false, disabled = false, readOnly = false, onOpen,
}: Props) {
  const input = useRef<HTMLInputElement>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const count = attachments.length + pending.length;

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (!files.length) return;
    setErrors([]);
    const { ok, errors: errs } = checkFiles(files, count);
    setErrors(errs);
    if (ok.length) onAdd(ok);
  };

  return (
    <div className="space-y-2">
      {count > 0 && (
        <ul className="space-y-1">
          {attachments.map((a) => (
            <li key={`a${a.id}`} className="flex items-center gap-2 text-sm">
              <Icon pdf={isPdf(a.mime)} />
              {onOpen ? (
                <button type="button" title={a.name} onClick={() => onOpen(a)}
                  className="min-w-0 truncate text-left text-primary hover:underline">{a.name}</button>
              ) : <span title={a.name} className="min-w-0 truncate">{a.name}</span>}
              <span className="flex-none text-xs text-muted">{formatSize(a.size)}</span>
              {!readOnly && (
                <button type="button" aria-label={`Remove ${a.name}`} disabled={disabled}
                  onClick={() => { setErrors([]); onRemove(a); }} className="ml-auto flex-none px-1 text-muted hover:text-danger">×</button>
              )}
            </li>
          ))}
          {pending.map((f, i) => (
            <li key={`p${i}${f.name}`} className="flex items-center gap-2 text-sm">
              <Icon pdf={isPdf(f.type)} />
              <span title={f.name} className="min-w-0 truncate">{f.name}</span>
              <span className="flex-none text-xs text-muted">{formatSize(f.size)}</span>
              {!readOnly && (
                <button type="button" aria-label={`Remove ${f.name}`} disabled={disabled}
                  onClick={() => { setErrors([]); onRemove(f); }} className="ml-auto flex-none px-1 text-muted hover:text-danger">×</button>
              )}
            </li>
          ))}
        </ul>
      )}
      {busy && <Spinner />}
      {errors.map((m) => <p key={m} role="alert" className="text-sm text-danger">{m}</p>)}
      {!readOnly && count < MAX_FILES && (
        <>
          <input ref={input} type="file" multiple accept={ACCEPT} className="hidden" onChange={onPick} />
          <Button variant="ghost" size="sm" disabled={disabled || busy} onClick={() => input.current?.click()}>Add files</Button>
        </>
      )}
    </div>
  );
}
